import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, ScrollView } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
// Using a simple avatar fallback to avoid prop type mismatches
import { SVG_COMPONENTS } from '@/assets/admin';
import { IMessageResident } from '@/types/admin/messages';

interface UnassignedRecipientsNoticeProps {
  recipients: IMessageResident[];
}

export const UnassignedRecipientsNotice: React.FC<UnassignedRecipientsNoticeProps> = ({ recipients }) => {
  const [open, setOpen] = useState(false);
  const unassigned = useMemo(() => {
    return (recipients || []).filter((r: any) => !(r?.leaseCustomers?.[0]?.account && Object.keys(r.leaseCustomers?.[0]?.account || {}).length > 0));
  }, [recipients]);

  if (!unassigned.length) return null;

  return (
    <View style={styles.container}>
      <TouchableOpacity style={styles.headerRow} onPress={() => setOpen(true)} activeOpacity={0.8}>
        <Text style={styles.title}>Unassigned recipients</Text>
        <View style={styles.countPill}>
          <Text style={styles.countText}>{unassigned.length}</Text>
          <Text style={styles.chevron}>{'>'}</Text>
        </View>
      </TouchableOpacity>
      <Text style={styles.subtitle}>
        The following residents do not have a resio account and will receive an email invitation to join the conversation.
      </Text>

      <Modal visible={open} animationType="fade" transparent={true} onRequestClose={() => setOpen(false)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Conflict details</Text>
              <TouchableOpacity onPress={() => setOpen(false)}>
                <SVG_COMPONENTS.X_CIRCLE width={20} height={20} color={ADMIN_COLORS.TEXT_SECONDARY} />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.modalBody} contentContainerStyle={styles.modalBodyContent}>
              <Text style={styles.modalIntro}>The following residents do not have a resio account and will receive an email invitation to join the conversation.</Text>
              <View style={styles.listWrap}>
                {unassigned.map((resident: any) => {
                  const lc = resident.leaseCustomers?.[0];
                  const displayName = lc?.first_name ? `${lc.first_name} ${lc.last_name}` : lc?.email;
                  const role = lc?.customerType === 'primary' ? 'resident' : 'guarantor';
                  const unit = resident.unitNumberSpace;
                  const status = (resident.statusType || '').toString();
                  const statusFormatted = status ? `${status.charAt(0).toUpperCase()}${status.slice(1)}` : '';
                  return (
                    <View key={resident.id} style={styles.itemRow}>
                      <View style={styles.simpleAvatar}>
                        <Text style={styles.simpleAvatarText}>{(lc?.first_name?.[0] || lc?.email?.[0] || '?').toUpperCase()}</Text>
                      </View>
                      <View style={styles.itemTextWrap}>
                        <Text style={styles.itemTitle} numberOfLines={1}>{displayName}</Text>
                        <Text style={styles.itemSubtitle} numberOfLines={1}>{statusFormatted} {role} - {unit}</Text>
                      </View>
                    </View>
                  );
                })}
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingTop: 12,
    marginBottom: 16,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 6,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  countPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  countText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  chevron: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginLeft: 2,
  },
  subtitle: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    lineHeight: 18,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    padding: 16,
  },
  modalCard: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderRadius: 12,
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  modalBody: {
    maxHeight: 420,
    minWidth: 320,
  },
  modalBodyContent: {
    padding: 16,
  },
  modalIntro: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 12,
  },
  listWrap: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 8,
    padding: 12,
    gap: 12,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  itemTextWrap: {
    flex: 1,
  },
  simpleAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    alignItems: 'center',
    justifyContent: 'center',
  },
  simpleAvatarText: {
    fontSize: 12,
    fontWeight: '700',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  itemTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  itemSubtitle: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
});

export default UnassignedRecipientsNotice;


